import { FC } from "react";
import { useAtomValue } from "jotai";
import { Chip } from "@mui/material";
import { positiveState, negativeState, neutralState, ortexColorState } from "@/lib/store";
import Module from "@/components/module/Module";
import { AIBullet } from "@/models/event-page";
import { toneColor } from "./helpers";
import styles from "./EventPageRevamp.module.scss";

interface AISummaryCardProps {
    title?: string;
    confidence?: string;
    headline?: string;
    bullets?: AIBullet[] | null;
    takeaway?: string;
    topics?: string[] | null;
    foot?: string;
    /** When true, render a clear "unavailable" state instead of an empty card. */
    unavailable?: boolean;
}

/**
 * Generic AI summary card used by both the statistics summary and (a variant of)
 * the no-transcript fill. Rendered inside the shared <Module> panel (the app's
 * standard card treatment) with MUI <Chip> badges, so it is visually consistent
 * with the rest of the app. Bullet HTML comes from a trusted internal endpoint
 * and only carries inline emphasis (<b>/<em>), so it is rendered via
 * dangerouslySetInnerHTML — mirroring the existing transcript rendering.
 */
const AISummaryCard: FC<AISummaryCardProps> = ({ title = "AI Summary", confidence, headline, bullets, takeaway, topics, foot = "AI-generated from historical price & event data · not investment advice", unavailable = false }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const neutral = useAtomValue(neutralState);
    const accent = useAtomValue(ortexColorState);

    const safeBullets = (bullets ?? []).filter((b) => b && b.text);
    const safeTopics = (topics ?? []).filter(Boolean);
    const hasContent = Boolean(confidence || headline || takeaway || safeBullets.length > 0 || safeTopics.length > 0);
    const showUnavailable = unavailable || !hasContent;

    return (
        <Module title={title} headerButton={{ content: <Chip label="BETA" size="small" variant="outlined" sx={{ color: accent, borderColor: accent, height: 18, fontSize: 9, fontWeight: 700, pointerEvents: "none" }} />, onClick: () => {} }}>
            <div className={styles.aiCard}>
                {showUnavailable ? (
                    <div className={styles.empty}>AI summary unavailable.</div>
                ) : (
                    <>
                        {confidence && <span className={styles.aiConfidence}>{confidence}</span>}
                        {headline && <p className={styles.aiHeadline}>{headline}</p>}
                        {safeBullets.length > 0 && (
                            <ul className={styles.aiBullets}>
                                {safeBullets.map((b, i) => (
                                    <li key={i}>
                                        <span className={styles.aiDot} style={{ background: toneColor(b.tone ?? "neutral", positive, negative, neutral) }} />
                                        <span dangerouslySetInnerHTML={{ __html: b.text ?? "" }} />
                                    </li>
                                ))}
                            </ul>
                        )}
                        {takeaway && (
                            <div className={styles.aiTakeaway}>
                                <span dangerouslySetInnerHTML={{ __html: takeaway }} />
                            </div>
                        )}
                        {safeTopics.length > 0 && (
                            <div className={styles.aiTopics}>
                                {safeTopics.map((t) => (
                                    <Chip key={t} label={t} size="small" variant="outlined" />
                                ))}
                            </div>
                        )}
                    </>
                )}
                <div className={styles.aiFoot}>{foot}</div>
            </div>
        </Module>
    );
};

export default AISummaryCard;
